import { Bell, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MultiSelect } from '@/components/ui/multi-select';
import { cn } from '@/components/utils/utils';
import { useI18n } from '@/hooks/useI18n';
import {
  EmailTemplateChannel,
  EmailTemplatePayload,
  isChannelEnabled,
} from '@/lib/admin/email-templates/models/email-template.model';
import {
  EMAIL_RECIPIENT_TOKENS,
  NOTIFICATION_RECIPIENT_TOKENS,
} from '@/lib/admin/email-templates/constants/recipient-tokens';
import {
  EmailSubjectPreview,
  NotificationToastPreview,
} from '@/lib/admin/email-templates/components/TemplatePreviews';
import type { VariableTargetProps } from '@/lib/admin/email-templates/hooks/useVariableInsertion';

interface TemplateChannelCardProps {
  channel: EmailTemplateChannel;
  values: EmailTemplatePayload;
  error?: string;
  onFieldChange: <K extends keyof EmailTemplatePayload>(
    field: K,
    value: EmailTemplatePayload[K]
  ) => void;
  /** Stringa vuota → NULL, per i campi testo nullable. */
  onNullableChange: (
    field: 'mailgun_draft_link' | 'redirect_url' | 'notification_message',
    value: string
  ) => void;
  registerTarget: (name: string, setValue: (value: string) => void) => VariableTargetProps;
}

/** Label + controllo + nota, con la stessa spaziatura in tutte le righe. */
const Field = ({
  label,
  htmlFor,
  children,
  hint,
  hintTone = 'muted',
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
  hint?: string;
  hintTone?: 'muted' | 'warning' | 'error';
}) => (
  <div className="flex flex-col gap-1.5">
    <Label
      htmlFor={htmlFor}
      className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
    >
      {label}
    </Label>
    {children}
    {hint && (
      <p
        className={cn(
          'text-[11px]',
          hintTone === 'error' && 'text-red-600',
          hintTone === 'warning' && 'text-amber-600',
          hintTone === 'muted' && 'text-gray-400'
        )}
      >
        {hint}
      </p>
    )}
  </div>
);

/**
 * Una delle due metà della scheda template: tutto ciò che riguarda un canale
 * (testo, destinatari, link) in una card sola, più l'anteprima di come il
 * messaggio arriverà a destinazione.
 *
 * Lo stato "attivo/spento" non è una colonna a DB: è la lista dei destinatari a
 * decidere se il canale parte, quindi il badge qui è derivato e cambia mentre si
 * modifica la multi-select.
 */
export const TemplateChannelCard = ({
  channel,
  values,
  error,
  onFieldChange,
  onNullableChange,
  registerTarget,
}: TemplateChannelCardProps) => {
  const { t } = useI18n();

  const isEmail = channel === 'email';
  const Icon = isEmail ? Mail : Bell;
  const enabled = isChannelEnabled(values, channel);

  const recipientOptions = (isEmail ? EMAIL_RECIPIENT_TOKENS : NOTIFICATION_RECIPIENT_TOKENS).map(
    (token) => ({
      value: token,
      label: t(`emailTemplates.recipientTokens.${token}` as 'emailTemplates.recipientTokens.admin'),
    })
  );

  const recipients = isEmail ? values.recipients : values.notification_recipients;

  return (
    <section className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      <header className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
        <Icon className={cn('h-4 w-4', isEmail ? 'text-blue-600' : 'text-violet-600')} aria-hidden />
        <h3 className="text-sm font-semibold text-gray-900">
          {isEmail ? t('emailTemplates.tabs.emails') : t('emailTemplates.channel.notification')}
        </h3>
        <Badge variant={enabled ? 'success' : 'outline'} className="ml-auto text-[11px] font-medium">
          {enabled
            ? t('emailTemplates.table.statusActive')
            : t('emailTemplates.table.statusInactive')}
        </Badge>
      </header>

      <div className="flex flex-col gap-4 p-4">
        {isEmail ? (
          <Field
            label={t('emailTemplates.fields.subject')}
            htmlFor="subject"
            hint={error}
            hintTone="error"
          >
            {/*
              Textarea e non Input: oggetto e messaggio sono spesso più lunghi
              della riga una volta risolte le variabili, e due righe visibili
              bastano a leggerli per intero senza scorrere dentro al campo.
            */}
            <Textarea
              id="subject"
              rows={2}
              value={values.subject}
              onChange={(event) => onFieldChange('subject', event.target.value)}
              placeholder={t('emailTemplates.fields.subjectPlaceholder')}
              className={cn('resize-y', error && 'border-red-500')}
              {...registerTarget('subject', (value) => onFieldChange('subject', value))}
            />
          </Field>
        ) : (
          <Field
            label={t('emailTemplates.fields.notificationMessage')}
            htmlFor="notification_message"
            hint={t('emailTemplates.fields.notificationMessageHint')}
          >
            <Textarea
              id="notification_message"
              rows={2}
              className="resize-y"
              value={values.notification_message ?? ''}
              onChange={(event) => onNullableChange('notification_message', event.target.value)}
              placeholder={t('emailTemplates.fields.notificationMessagePlaceholder')}
              {...registerTarget('notification_message', (value) =>
                onNullableChange('notification_message', value)
              )}
            />
          </Field>
        )}

        <Field
          label={
            isEmail
              ? t('emailTemplates.fields.recipients')
              : t('emailTemplates.fields.notificationRecipients')
          }
          hint={t(
            enabled
              ? isEmail
                ? 'emailTemplates.fields.recipientsEnabledHint'
                : 'emailTemplates.fields.notificationRecipientsEnabledHint'
              : isEmail
                ? 'emailTemplates.fields.recipientsDisabledHint'
                : 'emailTemplates.fields.notificationRecipientsDisabledHint'
          )}
          hintTone={enabled ? 'muted' : 'warning'}
        >
          <MultiSelect
            options={recipientOptions}
            value={recipients}
            onChange={(next) =>
              onFieldChange(isEmail ? 'recipients' : 'notification_recipients', next)
            }
            placeholder={t('emailTemplates.fields.recipientsPlaceholder')}
            searchPlaceholder={t('common.search')}
            emptyLabel={t('emailTemplates.fields.recipientsEmpty')}
            clearLabel={t('emailTemplates.fields.recipientsClear')}
            removeLabel={t('emailTemplates.fields.recipientsRemove')}
          />
        </Field>

        {isEmail ? (
          <Field label={t('emailTemplates.fields.mailgunDraftLink')} htmlFor="mailgun_draft_link">
            <Input
              id="mailgun_draft_link"
              value={values.mailgun_draft_link ?? ''}
              onChange={(event) => onNullableChange('mailgun_draft_link', event.target.value)}
              placeholder="https://"
              {...registerTarget('mailgun_draft_link', (value) =>
                onNullableChange('mailgun_draft_link', value)
              )}
            />
          </Field>
        ) : (
          <Field
            label={t('emailTemplates.fields.redirectUrl')}
            htmlFor="redirect_url"
            hint={t('emailTemplates.fields.redirectUrlHint')}
          >
            <Input
              id="redirect_url"
              value={values.redirect_url ?? ''}
              onChange={(event) => onNullableChange('redirect_url', event.target.value)}
              placeholder={t('emailTemplates.fields.redirectUrlPlaceholder')}
              {...registerTarget('redirect_url', (value) => onNullableChange('redirect_url', value))}
            />
          </Field>
        )}

        {isEmail ? (
          <EmailSubjectPreview subject={values.subject} />
        ) : (
          <NotificationToastPreview templateKey={values.template_key} />
        )}
      </div>
    </section>
  );
};
